// The board's handle, once, for everything that has to reach the renderer.
//
// Eight surfaces take it — the overlays, the labels, the gizmo, the hint card,
// the pause menu's camera buttons — and every one of them was handed it as a
// prop from `GameShell`. A context is the cheap answer *here specifically* and
// nowhere else: the ref **object** is created once and never replaced, so a
// consumer of this context never re-renders because of it. The thing inside it
// changes; the identity does not.
//
// That is the whole rule for what may live in a context in this shell. Game
// state may not — it moves every command, and a context is a re-render for
// every consumer. `useBoardInteraction`'s return may not — it is a fresh object
// per render. This may, because it is the one value that is genuinely constant.

import { createContext, useContext, type ReactNode, type RefObject } from "react";

import type { BoardCanvasHandle } from "../BoardCanvas";

type Handle = RefObject<BoardCanvasHandle | null>;

const BoardHandleContext = createContext<Handle | null>(null);

export const BoardHandleProvider = ({
  board,
  children
}: {
  board: Handle;
  children: ReactNode;
}) => (
  <BoardHandleContext.Provider value={board}>{children}</BoardHandleContext.Provider>
);

/**
 * The board's handle. Throws outside the provider rather than answering null:
 * a surface that quietly stopped reaching the renderer would simply never draw,
 * which is the silent no-op the whole interaction layer is written to avoid.
 */
export function useBoardHandle(): Handle {
  const board = useContext(BoardHandleContext);
  if (!board) throw new Error("useBoardHandle outside BoardHandleProvider");
  return board;
}
